import React, { useState } from "react";
import { HiOutlineMail, HiOutlinePhone } from "react-icons/hi";
import { apiUrl, brands } from "../config/config";
import SEO from "../components/SEO";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
    brand: brands,
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess("");
    setError("");

    try {
      const res = await fetch(`${apiUrl}/api/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Something went wrong");
      }

      setSuccess("Thank you! Your message has been sent successfully.");
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
        brand: brands,
      });
    } catch (err) {
      setError(err.message || "Failed to submit form");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Contact"
        description="Patch Designers For Hats"
        keywords="Patch Designers For Hats"
      />

      <div className="min-h-screen bg-gradient-to-b from-[#eef6ff] via-white to-[#fff4e8]">
        <div className="mx-auto w-full max-w-[1600px] px-4 py-10 sm:px-6 sm:py-12 lg:px-10 lg:py-14">
          {/* Header */}
          <div className="mx-auto mb-10 max-w-[900px] text-center sm:mb-12 lg:mb-14">
            <span className="mb-5 inline-flex items-center justify-center rounded-full border border-[#f97316]/20 bg-[#f97316]/10 px-5 py-2 text-[13px] font-bold uppercase tracking-[1px] text-[#f97316] shadow-sm sm:text-[14px]">
              Contact Patch Designers
            </span>

            <h1 className="text-[34px] font-extrabold leading-tight tracking-wide text-[#2b2b2b] sm:text-[46px] lg:text-[58px]">
              Contact Us
            </h1>

            <p className="mx-auto mt-5 max-w-[760px] text-[15px] font-medium leading-[28px] text-[#415164] sm:text-[17px] lg:text-[18px] lg:leading-[31px]">
              Have questions or need assistance? Reach out to our team at{" "}
              <strong className="font-extrabold text-[#0a2336]">
                Personalised Hat Patches
              </strong>
              .
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[0.9fr_1.1fr] lg:gap-8">
            {/* Contact Info */}
            <div className="rounded-[24px] border border-[#e5eaf0] bg-[rgba(31,44,76,0.95)] px-5 py-8 text-white shadow-[0_20px_50px_rgba(15,35,54,0.18)] sm:px-8 lg:px-10 lg:py-10">
              <h2 className="text-[28px] font-bold leading-tight tracking-wide text-white sm:text-[34px] lg:text-[40px]">
                Get in Touch
              </h2>

              <div className="mt-5 h-px w-full max-w-[420px] bg-white/30"></div>

              <p className="mt-6 text-[15px] font-medium leading-[27px] text-white/80 sm:text-[16px]">
                Our support team is ready to help you with patch design,
                ordering, production, and delivery questions.
              </p>

              <div className="mt-8 space-y-4">
                <a
                  href="mailto:support@personalisedhatpatches.com"
                  className="group flex items-center gap-4 rounded-[16px] border border-white/15 bg-white/10 px-4 py-4 transition-all duration-300 hover:border-[#f97316]/50 hover:bg-white/15"
                >
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#f97316] text-white">
                    <HiOutlineMail className="h-6 w-6" />
                  </span>

                  <span className="min-w-0 text-[14px] font-semibold leading-[22px] text-white sm:text-[15px]">
                    support@personalisedhatpatches.com
                  </span>
                </a>

                <a
                  href="tel:+17036668193"
                  className="group flex items-center gap-4 rounded-[16px] border border-white/15 bg-white/10 px-4 py-4 transition-all duration-300 hover:border-[#f97316]/50 hover:bg-white/15"
                >
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#f97316] text-white">
                    <HiOutlinePhone className="h-6 w-6" />
                  </span>

                  <span className="text-[14px] font-semibold leading-[22px] text-white sm:text-[15px]">
                    (703) 666-8193
                  </span>
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div className="rounded-[24px] border border-[#e5eaf0] bg-white px-5 py-8 shadow-[0_18px_50px_rgba(15,35,54,0.10)] sm:px-8 lg:px-10 lg:py-10">
              <h2 className="text-[28px] font-bold leading-tight tracking-wide text-[#2b2b2b] sm:text-[34px] lg:text-[40px]">
                Send Us a Message
              </h2>

              <div className="mt-5 h-px w-full max-w-[420px] bg-[#9ca3af]"></div>

              {success && (
                <div className="mt-6 rounded-[14px] border border-green-200 bg-green-50 px-4 py-3 text-[14px] font-semibold text-green-700">
                  {success}
                </div>
              )}

              {error && (
                <div className="mt-6 rounded-[14px] border border-red-200 bg-red-50 px-4 py-3 text-[14px] font-semibold text-red-700">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="mt-6 space-y-5">
                {["name", "email", "subject"].map((field) => (
                  <div key={field}>
                    <label className="mb-2 block text-[14px] font-bold capitalize text-[#0a2336]">
                      {field}
                    </label>

                    <input
                      type={field === "email" ? "email" : "text"}
                      name={field}
                      value={formData[field]}
                      onChange={handleChange}
                      required
                      className="h-[50px] w-full rounded-[14px] border border-[#dbe7f3] bg-[#f8fafc] px-4 text-[15px] font-medium text-[#2b2b2b] outline-none transition-all duration-300 placeholder:text-[#8a8a8a] focus:border-[#f97316]/60 focus:bg-white focus:ring-4 focus:ring-[#f97316]/10"
                    />
                  </div>
                ))}

                <div>
                  <label className="mb-2 block text-[14px] font-bold text-[#0a2336]">
                    Message
                  </label>

                  <textarea
                    name="message"
                    rows="5"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    className="w-full resize-none rounded-[14px] border border-[#dbe7f3] bg-[#f8fafc] px-4 py-3 text-[15px] font-medium leading-[26px] text-[#2b2b2b] outline-none transition-all duration-300 placeholder:text-[#8a8a8a] focus:border-[#f97316]/60 focus:bg-white focus:ring-4 focus:ring-[#f97316]/10"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-[14px] bg-[#0a2336] px-6 py-3.5 text-[15px] font-extrabold uppercase tracking-[0.5px] text-white shadow-[0_12px_30px_rgba(10,35,54,0.18)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#f97316] hover:shadow-[0_16px_35px_rgba(249,115,22,0.25)] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loading ? "Sending..." : "Send Message"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;